/* Ozzi scroll reveal.

   Loaded from templates/index.liquid (the live homepage, since 2026-09-09) and
   templates/page.sections-test.liquid. Deleting the two asset tags from a template removes
   the entire feature there: no section file is edited, only classes are added to markup
   that already exists. The hero rules in the CSS target #shopify-section-ozzi-hero only,
   so the homepage's packet hero is never touched.

   What it does: a short, hand-picked list of elements rise into place as they enter the
   viewport, and the social-proof number counts up from zero.

   The hero is above the fold, so the observer never touches it: nothing in the first paint
   is ever hidden. Its copy gets a short CSS keyframe sequence instead (see the hero block in
   ozzi-reveal.css), and the number in its subhead is counted up from here.

   Deliberately NOT animated, and each for a reason:
     - the claims marquee: above the fold, and it already moves.
     - the product builder: delaying a buy box to look clever is a conversion cost.
     - the Loox widget: a third party owns that DOM.
     - the progress timeline's line and dots: that section already scroll-drives its own
       fill (see the script at the bottom of sections/ozzi-progress-timeline.liquid). Only
       its heading and cards are touched here, so the two do not fight.

   The hide/reveal decision is made by the IntersectionObserver, never by measuring an
   element ourselves. An element is hidden only once the observer has reported it off
   screen, which means the visitor can never see something get hidden. See ozzi-reveal.css
   for the rest of the safety rules.
*/
(function () {
  'use strict';

  // Switch the feature off entirely rather than degrading it. The CSS hidden state is scoped
  // to the class added below, so returning here leaves the page completely static.
  // Two loaders can both emit this script (product.crave-crusher sits behind the theme.liquid
  // gate AND renders ozzi-v2-head). Second copy does nothing.
  if (window.ozsrLoaded) return;
  window.ozsrLoaded = true;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window) || !document.querySelectorAll) return;

  var sec = function (id) { return '#shopify-section-' + id + ' '; };

  // [selector, options]
  //   fade    — opacity only, no transform (scroll-snap children, or already transforming)
  //   stagger — index the matches so they arrive in sequence
  //   max     — cap how many get staggered; the rest reveal with no delay
  //   count   — count the first number in the text up from zero
  //   lead    — the longer travel used for a heading's supporting line
  //
  // Order matters: the first rule that matches an element wins, and an element already
  // claimed by an earlier rule is skipped.
  var TARGETS = [
    // Never hidden (it is in the first paint) — this entry exists only to count the number.
    // The 240ms matches the hero subhead's animation-delay in the CSS, so the count starts
    // as the line arrives rather than while it is still transparent.
    [sec('ozzi-hero') + '.hero-sub', { count: true, countDelay: 240 }],

    [sec('ozzi-review-strip') + 'h2', { count: true }],

    [sec('ozzi-fiber-cravings') + '.fiber-left > *', { stagger: true }],
    [sec('ozzi-fiber-cravings') + '.fiber-right .eyebrow, ' + sec('ozzi-fiber-cravings') + '.fiber-right h2', { stagger: true }],
    [sec('ozzi-fiber-cravings') + '.cell', { stagger: true, max: 6 }],

    [sec('ozzi-ingredients') + 'h2', {}],
    [sec('ozzi-ingredients') + '.sub', { lead: true }],
    [sec('ozzi-ingredients') + '.ing-slot', { fade: true, stagger: true, max: 6 }],

    [sec('ozzi-video-testimonials') + 'h2', {}],

    [sec('ozzi-progress-timeline') + '.ozt-title', {}],
    [sec('ozzi-progress-timeline') + '.ozt-sub', { lead: true }],
    [sec('ozzi-progress-timeline') + '.ozt-card', { stagger: true, max: 4 }],

    [sec('ozzi-how-to-use') + '.howto-head > *', { stagger: true }],
    [sec('ozzi-how-to-use') + '.step', { fade: true, stagger: true }],

    [sec('ozzi-faq') + 'h2', {}],
    [sec('ozzi-faq') + '.faq-sub', { lead: true }],
    [sec('ozzi-faq') + '.faq-item', { stagger: true, max: 6 }]
  ];

  var REVEAL_CLASS = 'ozsr';
  var IN_CLASS = 'is-in';

  /* ---------- count-up ----------
     Finds the first run of digits in the element's text and wraps just that run in a span,
     walking text nodes rather than rewriting innerHTML so any child markup survives. The
     finished string is byte-identical to what shipped, so the heading a crawler reads never
     changes; only the intermediate frames differ. */
  function prepCount(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var node, match;
    while ((node = walker.nextNode())) {
      match = /\d[\d,]*/.exec(node.nodeValue);
      if (match) break;
    }
    if (!node || !match) return null;

    var target = parseInt(match[0].replace(/,/g, ''), 10);
    if (!isFinite(target) || target < 10) return null;

    var span = document.createElement('span');
    span.className = 'ozsr-num';
    span.textContent = match[0];

    var after = node.splitText(match.index);
    after.nodeValue = after.nodeValue.slice(match[0].length);
    after.parentNode.insertBefore(span, after);

    // Reserve the final width before counting, so growing from "0" to "10,000" cannot
    // reflow the heading on every frame.
    var w = span.getBoundingClientRect().width;
    if (w) span.style.minWidth = w.toFixed(1) + 'px';

    return { span: span, target: target, done: false, delay: 0 };
  }

  function runCount(state) {
    if (!state || state.done) return;
    state.done = true;

    var DURATION = 900;
    var start = 0;

    function frame(now) {
      if (!start) start = now;
      var t = Math.min(1, (now - start) / DURATION);
      var eased = 1 - Math.pow(1 - t, 3); // easeOutCubic: fast, then settles
      state.span.textContent = Math.round(state.target * eased).toLocaleString('en-US');
      if (t < 1) requestAnimationFrame(frame);
    }

    state.span.textContent = '0';
    if (state.delay) setTimeout(function () { requestAnimationFrame(frame); }, state.delay);
    else requestAnimationFrame(frame);
  }

  /* ---------- observer ---------- */
  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var el = entry.target;

      if (entry.isIntersecting) {
        // Visible. Either it was hidden and this is its cue, or it was on screen from the
        // start and was never hidden at all. Both end here.
        el.classList.add(IN_CLASS);
        if (el.ozsrCount) runCount(el.ozsrCount);
        observer.unobserve(el);
        return;
      }

      if (el.ozsrArmed) return;

      // An element with no box (empty setting, display:none, a widget that has not rendered)
      // may never report isIntersecting, so arming it would hide it permanently. Drop it and
      // let it render normally. Caught by the suite: an empty FAQ subhead did exactly this.
      var box = entry.boundingClientRect;
      if (!box || box.width === 0 || box.height === 0) {
        observer.unobserve(el);
        return;
      }

      // Off screen, and it has a box. Hiding it now is invisible to the visitor by
      // definition, which is what makes the flash impossible.
      el.ozsrArmed = true;
      el.classList.add(REVEAL_CLASS);
    });
  }, {
    // Nothing reveals the moment a pixel clears the bottom edge. The element has to be at
    // least 40% of the way up the screen (Brandon, 2026-09-09: at the bottom edge it fires
    // while nobody is looking). The bottom-of-page check in init() is the safety net for
    // anything that can never climb that high.
    rootMargin: '0px 0px -40% 0px',
    threshold: 0
  });

  function init() {
    document.documentElement.classList.add('ozsr-on');

    var claimed = [];
    TARGETS.forEach(function (target) {
      var opts = target[1];
      var els = Array.prototype.slice.call(document.querySelectorAll(target[0]));
      var index = 0;

      els.forEach(function (el) {
        if (claimed.indexOf(el) !== -1) return; // first rule that matches an element wins
        claimed.push(el);

        if (opts.fade) el.classList.add('ozsr--fade');
        if (opts.lead) el.classList.add('ozsr--lead');
        if (opts.stagger && (!opts.max || index < opts.max)) {
          el.style.setProperty('--ozsr-i', String(index++));
        }
        if (opts.count) {
          el.ozsrCount = prepCount(el);
          if (el.ozsrCount) el.ozsrCount.delay = opts.countDelay || 0;
        }

        observer.observe(el);
      });
    });

    // Insurance for the 40% margin: an element sitting in the bottom 40% of the viewport
    // when the page is scrolled to its end could never reach the trigger line. When the
    // visitor hits the bottom, reveal whatever is still armed.
    var revealRest = function () {
      if (window.innerHeight + window.scrollY < document.documentElement.scrollHeight - 2) return;
      claimed.forEach(function (el) {
        if (el.ozsrArmed && !el.classList.contains(IN_CLASS)) {
          el.classList.add(IN_CLASS);
          if (el.ozsrCount) runCount(el.ozsrCount);
          observer.unobserve(el);
        }
      });
    };
    window.addEventListener('scroll', revealRest, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
